// Thin client for the service/main.py API. Every function here only ever
// sends/receives ciphertext, public keys, or the relay API token - never a
// password or private key. No analytics/error-reporting wired in either,
// so there's nowhere else for secrets to leak to.
//
// Auth is a bearer token (X-Api-Key) sent explicitly on every call - either
// the self-service ~1hr token from requestApiToken() below, or a permanent
// admin-issued key for the CLI/automation path. No cookies, no session.

export interface ApiConfig {
  baseUrl: string;
}

async function checkOk(resp: Response): Promise<Response> {
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    let detail = text;
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed?.detail === "string") detail = parsed.detail;
    } catch {
      // not JSON, fall back to the raw text below
    }
    throw new Error(`Request failed (${resp.status}): ${detail.slice(0, 200)}`);
  }
  return resp;
}

// -----------------------------------------------------------------------
// Playground helpers for the Service API page - let the in-app "try it
// live" panel fire the same requests an external integrator's code would.
// -----------------------------------------------------------------------

export async function health(baseUrl: string): Promise<{ status: string }> {
  const resp = await fetch(`${baseUrl}/health`);
  await checkOk(resp);
  return resp.json();
}

export async function fetchPublicKey(baseUrl: string, ownerId: string): Promise<Uint8Array> {
  const resp = await fetch(`${baseUrl}/public-keys/${encodeURIComponent(ownerId)}`);
  await checkOk(resp);
  return new Uint8Array(await resp.arrayBuffer());
}

export async function registerPublicKey(
  baseUrl: string,
  ownerId: string,
  apiKey: string,
  publicKey: Uint8Array,
): Promise<{ owner_id: string }> {
  const resp = await fetch(`${baseUrl}/public-keys/${encodeURIComponent(ownerId)}`, {
    method: "POST",
    headers: { "x-api-key": apiKey, "content-type": "application/octet-stream" },
    body: publicKey as Uint8Array<ArrayBuffer>,
  });
  await checkOk(resp);
  return resp.json();
}

// Self-service: anyone can request a token, no identity check.
export async function requestApiToken(baseUrl: string): Promise<{ api_key: string; expires_at: number }> {
  const resp = await fetch(`${baseUrl}/api-tokens`, { method: "POST" });
  await checkOk(resp);
  return resp.json();
}

export async function uploadBlob(cfg: ApiConfig, blob: Blob, apiToken: string): Promise<{ blob_id: string; size: number }> {
  const resp = await fetch(`${cfg.baseUrl}/blobs`, {
    method: "POST",
    headers: { "x-api-key": apiToken, "content-type": "application/octet-stream" },
    body: blob,
  });
  await checkOk(resp);
  return resp.json();
}

export async function downloadBlob(cfg: ApiConfig, blobId: string, apiToken: string): Promise<Blob> {
  const resp = await fetch(`${cfg.baseUrl}/blobs/${encodeURIComponent(blobId)}`, {
    headers: { "x-api-key": apiToken },
  });
  await checkOk(resp);
  return resp.blob();
}
